/**
 * Account-linking API client (AL-M3).
 *
 * Thin, framework-free `fetch` wrappers over the AL-M2 HTTP surface
 * (`/v1/auth/link/*`, `/v1/auth/password/set-initial`). The shared package is
 * product-agnostic, so every call takes an explicit `apiBase` (the product
 * passes `import.meta.env.VITE_API_BASE_URL`). Bearer-authed calls take a
 * `token` (the Cognito access token from `useAuth().accessToken`).
 *
 * All endpoints use the Hub `{ok, data}` / `{ok, error}` envelope. We translate
 * that into a discriminated `ApiResult<T>` so callers branch on a typed `error`
 * code + HTTP `status` rather than parsing strings.
 *
 * Endpoint + error-code contract: docs/AL-M2/DESIGN.md §Handoff.
 */
async function call(url, init) {
    let res;
    try {
        res = await fetch(url, init);
    }
    catch {
        return { ok: false, error: "network_error", status: 0 };
    }
    let body = {};
    try {
        body = (await res.json());
    }
    catch {
        // Non-JSON (e.g. gateway 5xx) — synthesize an error from the status.
        return { ok: false, error: `http_${res.status}`, status: res.status };
    }
    if (!res.ok || body.ok === false) {
        return {
            ok: false,
            error: body.error ?? `http_${res.status}`,
            status: res.status,
            // Pass the whole envelope through so callers can read extra error fields
            // (e.g. the 422 invalid_code `attemptsLeft`).
            details: body.details ?? body,
        };
    }
    return { ok: true, data: (body.data ?? {}) };
}
function authHeaders(token) {
    return { "Content-Type": "application/json", Authorization: `Bearer ${token}` };
}
const JSON_HEADERS = { "Content-Type": "application/json" };
export function lookupLink(apiBase, requestId) {
    const q = encodeURIComponent(requestId);
    return call(`${apiBase}/v1/auth/link/lookup?req=${q}`, {
        method: "GET",
        headers: JSON_HEADERS,
    });
}
export function requestLinkCode(apiBase, requestId) {
    return call(`${apiBase}/v1/auth/link/request`, {
        method: "POST",
        headers: JSON_HEADERS,
        body: JSON.stringify({ requestId }),
    });
}
export function confirmLink(apiBase, requestId, code) {
    return call(`${apiBase}/v1/auth/link/confirm`, {
        method: "POST",
        headers: JSON_HEADERS,
        body: JSON.stringify({ requestId, code }),
    });
}
export function addProvider(apiBase, token, args) {
    return call(`${apiBase}/v1/auth/link/add-provider`, {
        method: "POST",
        headers: authHeaders(token),
        body: JSON.stringify(args),
    });
}
export function addProviderCallback(apiBase, token, args) {
    return call(`${apiBase}/v1/auth/link/add-provider/callback`, {
        method: "POST",
        headers: authHeaders(token),
        body: JSON.stringify(args),
    });
}
export function removeProvider(apiBase, token, providerName) {
    const p = encodeURIComponent(providerName);
    return call(`${apiBase}/v1/auth/link/provider/${p}`, { method: "DELETE", headers: authHeaders(token) });
}
export function setInitialPassword(apiBase, token, password) {
    return call(`${apiBase}/v1/auth/password/set-initial`, {
        method: "POST",
        headers: authHeaders(token),
        body: JSON.stringify({ password }),
    });
}
/**
 * Mint a one-time SSO handoff code for `target` (product id, e.g. "qr").
 * Works with ANY signed-in product's token — hub→app and app→app alike.
 */
export function createSsoHandoff(apiBase, token, target) {
    return call(`${apiBase}/v1/sso/handoff`, {
        method: "POST",
        headers: authHeaders(token),
        body: JSON.stringify({ target }),
    });
}
